import { IsStringEmpty, isStringNotEmpty } from '../utils/GlobalFunctions'
import { URLConstructor } from '../utils/URLBuilder'
import { AkixiEntities } from './AkixiEntities'
import { TelSysTypes } from './TelephonyServerTypes'
import { DASH, EMPTY_STRING } from './Common'
import { ADVANCED_FIELDS } from './TelSysConstants'
import { SLASH } from './Separators'
import { ANY_VALUE_STRING_NUMERIC } from '../common/DefaultValues'

type CallDetailsInfo = {
    fallbackID?: string
    itemID?: string
    itemIDNum?: number
    start?: string
    end?: string
}

export const CACHE_TIME: number = 10000
export const NO_CACHE_TIME: number = 0
export const EXTENDED_CACHE_TIME: number = 500000

// URL elements.
export enum APIElements {
    CCS = 'CCS',
    API = 'API',
    APP_V2 = 'App/v2',
    API_VERSION = 'v1',
    API_INTERNAL = 'internal',
    API_ADMIN = 'admin',
    API_INTEGRATION = 'integration',
    API_APP = 'App',
    AKIXI_2019 = 'Akixi2019'
}

// Path params.
export enum APIPathParams {
    ACTION = 'action',
    RESULT = 'result',
    CURRENT_USER = 'currentUser',
    STATUS = 'status',
    DETAIL = 'detail',
    INFO = 'info',
    CALL_RECORDING_INTEGRATIONS = 'callRecordingIntegration',
    CONFIG = 'config',
    MAKE_CALL = 'makecall',
    TRANSFER_ONE_STEP = 'transferonestep',
    DIVERT_DEFLECT = 'divertdeflectconnection',
    INQUIRY_TRANSFER_TO = 'makeconsultationcall',
    PICKUP_CONNECTION = 'pickupconnection',
    SET_DND = 'setdnd',
    COMPLETE_TRANSFER = 'transfercomplete',
    CME = 'cme',
    SESSION = 'session',
    TEST_CONNECTION = 'testConnection',
    FEATURE = 'feature',
    INSIGHTS = 'insights',
    REPORT = 'report',
    SET_ACC_CODE = 'setacccode',
    FINISH = 'finish',
    TRACKER = 'tracker',
    INVITE = 'invite',
    SETTINGS = 'settings',
    NOTIFICATION_PUSH_TOKEN = 'notificationPushToken',
    BRANDING = 'branding',
    LIST = 'list',
    USERNAME = 'username',
    DEFAULT_REPORT = 'defaultReport',
    REPORT_SET = 'defaultReportSet',
    MOVE_TO_BIN = 'moveToBin',
    BROADWORKS = 'broadworks',
    AUTHENTICATE = 'authenticate',
    ID_ON_TEL_SYS = 'idOnTelSys',
    SIGNUP = 'signUp',
    UTILITIES = 'utilities',
    TIMEZONE = 'timezone',
    DEFAULT = 'default',
    LICENCE = 'license',
    OAUTH = 'oauth',
    INTEGRATION = 'integration',
    CALL_CONTROL_OPTIONS = 'callControlOptions',
    SEND_EMAIL = 'sendEmail',
    LICENCES = 'licenses',
    CHANGE_PASSWORD = 'changePassword',
    RESET_PASSWORD = 'resetPassword',
    PASSWORD = 'password',
    GENERATE = 'generate',
    STRENGTH = 'strength',
    CALL_CAPTURE = 'callCapture',
    HIDE_INSIGHTS_HUB = 'hideInsightsHub',
    SHOW_INSIGHTS_HUB = 'showInsightsHub',
    WEBSOCKET = 'websocket',
    PRESENCE = 'presence',
    FILTER_OPTION = 'filterOption',
    DEVICE_TYPES = 'deviceTypes',
    LOCALES = 'locales',
    FIRST_SYNC_STATUS = 'firstSyncStatus',
    API_KEYS = 'apiKeys',
    USER = 'user',
    ROUTE = 'route',
    AUTH_OUTFLOW = 'authOutflow',
    ACCEPT_TERMS_OF_SERVICE = 'acceptTermsOfService',
    REJECT_TERMS_OF_SERVICE = 'rejectTermsOfService',
    LOGIN_LANDING = 'loginLanding',
    SERVICES = 'services',
    LANGUAGE = 'language',
    LOGIN = 'login',
    RECAPTCHA = 'recaptcha',
    FORGOT_CREDENTIALS = 'forgotCredential',
    FAVOURITES = 'favourites',
    DARK_MODE = 'darkmode',
    MFA_SUCCESS = 'mfaSuccess',
    USER_AGENTS = 'userAgents',
    USER_AGENT = 'userAgent',
    BRANDING_CONFIG = 'brandingConfig',
    TOKEN = 'token',
    VIDEO = 'video',
    INTEGRATOR_CONFIG = 'integratorClient',
    CRM_CONFIGURATION = 'crm',
    CONFIGS = 'configs',
    TYPES = 'types',
    TEMPLATE = 'template'
}

enum DNDActions {
    DND_ON = 'on',
    DND_OFF = 'off'
}

// Query params.
enum QueryParams {
    DEVICE_TYPE_FILTER = 'deviceTypeFilter',
    GROUP_TYPE_FILTER = 'groupTypeFilter',
    CODE_TYPE_FILTER = 'codeTypeFilter',
    SORT = 'sort',
    RECURSIVE = 'recursive',
    TEL_SYS_FIELDS = 'telSysFields',
    ADVANCED_FIELDS_PARAM = 'advancedFields',
    TEL_SYS_ID = 'telSysID',
    PARTITION_FIELDS = 'partitionFields',
    DEVICE_FIELDS = 'deviceFields',
    AGENT_FIELDS = 'agentFields',
    CODE = 'code',
    FILTER_BY_PARTITION_LICENCE = 'filterByPartitionLicence',
    DEFAULT_BRANDING_STYLE = 'defaultBrandingStyle',
    DEFAULT_REPORT_TYPE = 'defaultReportType',
    DEFAULT_REPORT_NAME = 'defaultReportName',
    MOVE_TO_BIN = 'moveToBin',
    MONITORED_ONLY = 'monitoredOnly',
    LICENCE_SETTING_FIELDS = 'licenseNames',
    SEARCH_QUERY = 'searchQuery',
    CRM_TYPE = 'crmType',
    TYPE = 'type',
    IP_ADDRESS_OR_HOSTNAME = 'ipAddressOrHostname',
    IANA = 'IANA',
    BRANDING_ID = 'brandingId',
    CME_FIELDS = 'CMEnvironmentFields',
    OFFSET = 'offset',
    LIMIT = 'limit',
    INTEGRATOR_CLIENT_FIELDS = 'IntegratorClientFields',
    CRM_CONFIGURATION_FIELDS = 'crmConfigurationFields',
    CME_ID = 'CMEnvironmentID'
}

// MFA path params.
enum MFAPathParams {
    MFA = 'mfa',
    SECRET_CODE = 'secretCode',
    SETUP = 'mfaSetup',
    ENABLE = 'enableMfa',
    OTP = 'otp'
}

// Headers.
export const INVOKE_ID_HEADER: string = 'X-InvokeID'

// Actions.

// Connection control.
export enum ConnectionControls {
    ANSWER_CALL = 'answercall',
    CLEAR_CONNECTION = 'clearconnection',
    CLEAR_CALL_MODEL = 'clearcallmodel'
}

// Sign in/out.
export enum SignInOut {
    SIGN_IN = 'signin',
    SIGN_OUT = 'signout'
}

// Status updates.
export enum ACDStatusUpdates {
    AVAILABLE = 'setavailable',
    WRAP_UP = 'setwrapup',
    DND = 'setacddnd',
    NOT_AVAILABLE = 'setnotavailable'
}

// Monitoring actions.
export enum MonitoringActions {
    MONITOR_CONNECTION = 'monitorconnection',
    RECORD_CALL = 'recordcall',
    REGISTER_CALLBACK = 'registercallback'
}

// Sorting.
export enum SortingParameters {
    DESCRIPTION_ASC = 'Description:ASC'
}

export enum EntityFields {
    COMMS_STATUS = 'CommsStatus'
}

export enum LogInOut {
    LOG_IN = 'login',
    LOG_OUT = 'logout'
}

export enum SSOLogIn {
    SSO = 'sso'
}

export enum TelSysFields {
    PARTITION_LICENCES = 'PartitionLicences',
    TELSYS_TYPE = 'TelSysType'
}

export enum CMEFields {
    CAPABILITIES = 'Capabilities'
}

// Filter joiner function.
const filterJoiner = (filters?: string[]): Definable<string> => filters?.join(DASH)

// Router base path.
export const getAppV2APIBasePath = (): URLConstructor =>
    URLConstructor.newInstance({ pathParams: [APIElements.CCS, APIElements.APP_V2] })

// API base.
export const getAPIBasePath = (): URLConstructor =>
    URLConstructor.newInstance({ pathParams: [APIElements.CCS, APIElements.API, APIElements.API_VERSION] })

// Base path.
export const getAPIInternalBaseURL = (): URLConstructor => getAPIBasePath().appendPathParams(APIElements.API_INTERNAL)

// Integration path.
export const getAPIIntegrationBaseURL = (): URLConstructor =>
    getAPIBasePath().appendPathParams(APIElements.API_INTEGRATION)

// Base Servlet Path.
export const getBaseServletPath = (urlMapping?: string): URLConstructor =>
    URLConstructor.newInstance({
        pathParams: [APIElements.CCS, APIElements.API_APP, urlMapping ?? APIElements.AKIXI_2019]
    })

// Admin Base path.
export const getAPIAdminBasePath = (): URLConstructor => getAPIBasePath().appendPathParams(APIElements.API_ADMIN)

// User info endpoint.
export const userInfoURL: URLConstructor = getAPIBasePath().appendPathParams(APIPathParams.CURRENT_USER)

// Session API.
export const sessionURL: URLConstructor = getAPIBasePath().appendPathParams(APIPathParams.SESSION)

// Telsys API internal.
export const telSysInternalURL: URLConstructor = getAPIInternalBaseURL().appendPathParams(AkixiEntities.TEL_SYS)

// Entity creator URL.
export const telSysCreatorURL: URLConstructor = getAPIAdminBasePath().appendPathParams(AkixiEntities.TEL_SYS)
export const telServerWithCommStatusURL: URLConstructor = telSysCreatorURL.appendQueryParams({
    [QueryParams.TEL_SYS_FIELDS]: EntityFields.COMMS_STATUS
})
export const partitionCreatorURL = (telSysID: number | string): URLConstructor =>
    getAPIAdminBasePath().appendPathParams(AkixiEntities.TEL_SYS, telSysID, AkixiEntities.PARTITION)
export const partitionsWithCommStatusURL = (telSysID: number | string): URLConstructor =>
    partitionCreatorURL(telSysID).appendQueryParams({
        [QueryParams.PARTITION_FIELDS]: EntityFields.COMMS_STATUS
    })

// Entity update URL.
export const telSysUpdateURL = (telSysID: number | string): URLConstructor =>
    telSysCreatorURL.appendPathParams(telSysID)
export const telSysByIDInteralURL = (telSysID: number | string): URLConstructor =>
    telSysInternalURL.appendPathParams(telSysID)
export const telSysFirstSyncURL = (telSysID: number | string): URLConstructor =>
    telSysInternalURL.appendPathParams(telSysID).appendPathParams(APIPathParams.FIRST_SYNC_STATUS)
export const partitionUpdateURL = (telSysID: number | string, partitionID: string | number): URLConstructor =>
    partitionCreatorURL(telSysID).appendPathParams(partitionID)

// Base URL creators.
export const partitionsBaseURL = (telSysID: string | number): URLConstructor =>
    getAPIInternalBaseURL().appendPathParams(AkixiEntities.TEL_SYS, telSysID, AkixiEntities.PARTITION)
export const partitionBaseURL = (telSysID: string | number, partitionID: string | number): URLConstructor =>
    getAPIInternalBaseURL().appendPathParams(AkixiEntities.TEL_SYS, telSysID, AkixiEntities.PARTITION, partitionID)

export const partitionAdminBaseURL = (telSysID: string | number): URLConstructor =>
    getAPIAdminBasePath().appendPathParams(AkixiEntities.TEL_SYS, telSysID, AkixiEntities.PARTITION)
const deviceBaseURL = (telSysID: string, partitionID: string, deviceID?: string): URLConstructor =>
    partitionBaseURL(telSysID, partitionID).appendPathParams(AkixiEntities.DEVICE, deviceID ?? '')
const deviceAdminBaseURL = (telSysID: string, partitionID: string, deviceID?: string): URLConstructor =>
    partitionAdminBaseURL(telSysID).appendPathParams(partitionID, AkixiEntities.DEVICE, deviceID ?? '')
const agentBaseURL = (telSysID: string | number, partitionID: string | number, agentID?: string): URLConstructor =>
    partitionBaseURL(telSysID, partitionID).appendPathParams(AkixiEntities.ACD_AGENTS, agentID ?? '')
const recordingBaseURL = (telSysID: string, telSysCallID: string, recordingID?: string): URLConstructor =>
    getAPIInternalBaseURL().appendPathParams(
        AkixiEntities.TEL_SYS,
        telSysID,
        AkixiEntities.CALL,
        telSysCallID,
        AkixiEntities.RECORDING,
        recordingID ?? ''
    )

// Get API URLs.
export const devicesURL = (
    telSysID: string | number,
    partitionID: string | number,
    monitoredOnly: boolean,
    filters?: string[],
    deviceFields?: string[],
    searchQuery?: string,
    sendFiltersWithComma?: boolean
): URLConstructor =>
    partitionBaseURL(telSysID, partitionID)
        .appendPathParams(AkixiEntities.DEVICE)
        .appendQueryParams({
            [QueryParams.DEVICE_TYPE_FILTER]: sendFiltersWithComma ? filters?.join(',') : filterJoiner(filters),
            [QueryParams.DEVICE_FIELDS]: deviceFields?.join(','),
            [QueryParams.MONITORED_ONLY]: monitoredOnly ? 'true' : 'false',
            ...(isStringNotEmpty(searchQuery) ? { [QueryParams.SEARCH_QUERY]: searchQuery } : {})
        })
export const deviceURL = (telSysID: string, partitionID: string, deviceID: string): URLConstructor =>
    deviceAdminBaseURL(telSysID, partitionID, deviceID)
export const accountCodesURL = (telSysID: string, paritionID: string, filters?: string[]): URLConstructor =>
    partitionBaseURL(telSysID, paritionID)
        .appendPathParams(AkixiEntities.CODES)
        .appendQueryParams({ [QueryParams.CODE_TYPE_FILTER]: filterJoiner(filters) })
export const groupsURL = (telSysID: string, partitionID: string, agentID: string, filters?: string[]): URLConstructor =>
    agentBaseURL(telSysID, partitionID, agentID)
        .appendPathParams(AkixiEntities.GROUPS)
        .appendQueryParams({ [QueryParams.GROUP_TYPE_FILTER]: filterJoiner(filters) })
export const codesURL = (telSysID: string, paritionID: string, agentID: string, filters?: string[]): URLConstructor =>
    agentBaseURL(telSysID, paritionID, agentID)
        .appendPathParams(AkixiEntities.CODES)
        .appendQueryParams({ [QueryParams.CODE_TYPE_FILTER]: filterJoiner(filters) })
export const agentsURL = (
    telSysID: string | number,
    partInTelSys: string | number,
    monitoredOnly: boolean,
    agentFields?: string[],
    searchQuery?: string
): URLConstructor =>
    partitionBaseURL(telSysID, partInTelSys)
        .appendPathParams(AkixiEntities.ACD_AGENTS)
        .appendQueryParams({
            [QueryParams.AGENT_FIELDS]: agentFields?.join(','),
            [QueryParams.MONITORED_ONLY]: monitoredOnly ? 'true' : 'false',
            ...(isStringNotEmpty(searchQuery) ? { [QueryParams.SEARCH_QUERY]: searchQuery } : {})
        })
export const agentStatusURL = (telSysID: string, partitionID: string, deviceID: string): URLConstructor =>
    deviceBaseURL(telSysID, partitionID, deviceID).appendPathParams(APIPathParams.STATUS)

// Get current user info.
export const getCurrentUserURL = (): URLConstructor => getAPIBasePath().appendPathParams(APIPathParams.CURRENT_USER)
export const fallbackMenuURL = (servletPath: string, servletCmd: string): URLConstructor =>
    URLConstructor.newInstance({
        pathParams: [servletPath],
        queryParams: { ServletCmd: servletCmd }
    })

// Action URLS.
export const currentUserActionURL = (action: string): URLConstructor =>
    getAPIInternalBaseURL().appendPathParams(
        APIPathParams.CURRENT_USER,
        AkixiEntities.DEVICE,
        APIPathParams.ACTION,
        action
    )
export const actionAgentActionURL = (
    telSysID: string | number,
    partitionID: string | number,
    agentID: string,
    action: string
): URLConstructor => agentBaseURL(telSysID, partitionID, agentID).appendPathParams(APIPathParams.ACTION, action)
export const actionDeviceActionURL = (
    telSysID: string,
    partitionID: string,
    action: string,
    deviceID?: string
): URLConstructor => deviceBaseURL(telSysID, partitionID, deviceID).appendPathParams(APIPathParams.ACTION, action)
export const statusCallActionURL = (action: string): URLConstructor =>
    getAPIInternalBaseURL().appendPathParams(APIPathParams.ACTION, action, APIPathParams.RESULT)

// Get contact.
export const contactStatusURL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    AkixiEntities.CONTACT,
    APIPathParams.RESULT
)
export const contactURL = (telSysID: string, partitionID: string, callDetails?: CallDetailsInfo): URLConstructor => {
    if (callDetails) {
        const { fallbackID, itemID, itemIDNum, start: startTime, end: endTime }: CallDetailsInfo = callDetails
        const contactURLObject: URLConstructor = partitionBaseURL(telSysID, partitionID).appendPathParams(
            AkixiEntities.CONTACT
        )

        if (IsStringEmpty(itemID)) {
            return contactURLObject.appendQueryParams({
                fallbackID,
                itemIDNum,
                startTime,
                endTime
            })
        }

        return contactURLObject.appendPathParams(itemID).appendQueryParams({
            startTime,
            endTime
        })
    }

    return URLConstructor.newInstance()
}

export const callDetailsStatusURL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    AkixiEntities.CALL_DETAILS,
    APIPathParams.RESULT
)

export const callDetailsURL = (
    telSysID: string,
    partitionID: string,
    callDetails?: CallDetailsInfo
): URLConstructor => {
    if (callDetails) {
        const { fallbackID, start: startTime, end: endTime }: CallDetailsInfo = callDetails
        const contactURLObject: URLConstructor = partitionBaseURL(telSysID, partitionID).appendPathParams(
            AkixiEntities.CALL_DETAILS
        )

        return contactURLObject.appendPathParams(fallbackID as string).appendQueryParams({
            startTime,
            endTime
        })
    }

    return URLConstructor.newInstance()
}

// Get ACD & DND activity.
export const ACDDNDActivityStatusURL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    AkixiEntities.ACDDND,
    APIPathParams.DETAIL,
    APIPathParams.RESULT
)
export const ACDDNDActivityURL = (
    telSysID: string,
    partitionID: string,
    ACDDNDActivityItemID: string,
    entryTime: string
): string => {
    if (telSysID && partitionID && ACDDNDActivityItemID) {
        return partitionBaseURL(telSysID, partitionID)
            .appendPathParams(AkixiEntities.ACDDND, ACDDNDActivityItemID)
            .appendQueryParams({ entryTime })
            .constructURL()
    }

    return ''
}

// Get and Modify Directory Details
export const getDirectoryURL = (telSysId: string, partitionInTelSys: string): URLConstructor =>
    getAPIAdminBasePath().appendPathParams(
        AkixiEntities.TEL_SYS,
        telSysId,
        AkixiEntities.PARTITION,
        partitionInTelSys,
        AkixiEntities.DIRECTORY
    )

export const addDirectoryURL = (telSysID: string, partitionInTelSys: string): URLConstructor =>
    getDirectoryURL(telSysID, partitionInTelSys)

export const getDirectoryByTelNumber = (
    telSysId: string,
    partitionInTelSys: string,
    telNumber: string
): URLConstructor => getDirectoryURL(telSysId, partitionInTelSys).appendPathParams(telNumber)

export const updateDirectoryURL = (telSysId: string, partitionInTelSys: string, telNumber: string): URLConstructor =>
    getDirectoryByTelNumber(telSysId, partitionInTelSys, telNumber)

// Get call recordings list.
export const recordingStatusURL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    AkixiEntities.CALL,
    AkixiEntities.RECORDING,
    APIPathParams.RESULT
)

export const recordingsURL = (
    telSysID: string,
    telSysCallID: string,
    recordingID?: string,
    // TODO: when we transition away from the old JS make this either number or string only, it doesn't matter which.
    recordingStart?: number | string,
    capabilities?: Array<string>,
    numberToAssign?: number
): string => {
    const queryParams: Record<string, Definable<string | number | string[]>> = {
        startTime: recordingStart,
        numberToAssign,
        URLCapabilities: capabilities
    }

    return recordingBaseURL(telSysID, telSysCallID, recordingID).appendQueryParams(queryParams).constructURL()
}
export const recordingDetailsStatusURL: string = getAPIInternalBaseURL()
    .appendPathParams(AkixiEntities.CALL, AkixiEntities.RECORDING, APIPathParams.DETAIL, APIPathParams.RESULT)
    .constructURL()

// Get application user details.
export const userURL = (): URLConstructor => getAPIAdminBasePath().appendPathParams(AkixiEntities.USER)
export const getByUsernameURL = (username: string): URLConstructor => userURL().appendPathParams(username)
export const usernameExistsURL = (username: string): URLConstructor =>
    getAPIBasePath().appendPathParams(APIPathParams.USERNAME, username)
export const sendEmailURL = (username: string): URLConstructor =>
    getByUsernameURL(username).appendPathParams(APIPathParams.SEND_EMAIL)

// Call Recording Integration
export const callRecIntegrationURL = (
    telSysID: string,
    partitionID: string | number,
    basePath: URLConstructor = getAPIAdminBasePath()
): URLConstructor =>
    basePath.appendPathParams(
        AkixiEntities.TEL_SYS,
        telSysID,
        AkixiEntities.PARTITION,
        partitionID,
        APIPathParams.CALL_RECORDING_INTEGRATIONS
    )

export const callRecIntegrationByGUIDURL = (integrationGUID: string): URLConstructor =>
    getAPIAdminBasePath().appendPathParams(APIPathParams.CALL_RECORDING_INTEGRATIONS, integrationGUID)

export const testCallRecIntegrationURL = (telSysID: string, partitionID: string): URLConstructor =>
    callRecIntegrationURL(telSysID, partitionID, getAPIInternalBaseURL()).appendPathParams(
        APIPathParams.TEST_CONNECTION
    )

export const testCallRecIntegrationResultURL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.CALL_RECORDING_INTEGRATIONS,
    APIPathParams.TEST_CONNECTION,
    APIPathParams.RESULT
)

export const callRecIntegrationConfigUrl = (
    telSysID: string,
    partitionID: string | number,
    integrationType: string,
    hostname: string
): URLConstructor =>
    callRecIntegrationURL(telSysID, partitionID, getAPIInternalBaseURL())
        .appendPathParams(APIPathParams.CONFIG)
        .appendQueryParams({
            [QueryParams.TYPE]: integrationType,
            [QueryParams.IP_ADDRESS_OR_HOSTNAME]: hostname
        })

// Get application Code details
export const addCodeURL = (telSysId: string, partitionInTelSys: string): URLConstructor =>
    partitionCreatorURL(telSysId).appendPathParams(partitionInTelSys, AkixiEntities.CODES)

export const updateCodeURL = (
    telSysId: string,
    partitionInTelSys: string,
    codeID: string,
    codeType: string
): URLConstructor => addCodeURL(telSysId, partitionInTelSys).appendPathParams(codeID, codeType)

export const getCodeByCodeNameURL = (
    telSysID: string,
    partitionID: string,
    code: string,
    codeType: string | number
): URLConstructor => partitionCreatorURL(telSysID).appendPathParams(partitionID, AkixiEntities.CODES, code, codeType)

// CME Details.
export const cmeAdminBaseURL = (): URLConstructor => getAPIAdminBasePath().appendPathParams(AkixiEntities.CME)
export const cmeDetailsByIDURL = (cmeID: string): URLConstructor =>
    cmeAdminBaseURL()
        .appendPathParams(cmeID)
        .appendQueryParams({ CMEnvironmentFields: ['BrandingConfig'] })
export const cmeDetailsByIDInternalURL = (cmeID: string): URLConstructor =>
    cmeInternalBaseURL().appendPathParams(cmeID, APIPathParams.INFO)

// Directory Entry
export const directoryEntryBaseURL = (telSys: string, partition: string): URLConstructor =>
    partitionBaseURL(telSys, partition).appendPathParams(AkixiEntities.DIRECTORY)
export const directoryEntryByIdURL = (telSysID: string, partitionID: string, telNo: string): URLConstructor =>
    directoryEntryBaseURL(telSysID, partitionID).appendPathParams(telNo)
// RequestGetJSON.
export const requestGetJsonURL = (params: Record<string, string>): URLConstructor =>
    getBaseServletPath().appendQueryParams(params)

// User roles
export const userRoleURL = (roleID?: string): URLConstructor =>
    getAPIAdminBasePath().appendPathParams(AkixiEntities.USER_ROLE, ...(roleID ? [roleID] : []))

// CME Estate details.
export const cmeInternalBaseURL = (): URLConstructor => getAPIInternalBaseURL().appendPathParams(AkixiEntities.CME)
export const cmeInternalGet = (cmeID: string): URLConstructor => cmeInternalBaseURL().appendPathParams(cmeID)
export const cmeDetailsInfoURL = (cmeID: string): URLConstructor =>
    cmeInternalBaseURL().appendPathParams(cmeID, APIPathParams.INFO)

// Make a call.
export const makeCallStatusURL: URLConstructor = statusCallActionURL(APIPathParams.MAKE_CALL)
export const makeCallMainDeviceURL = (): string => currentUserActionURL(APIPathParams.MAKE_CALL).constructURL()
export const makeCallFromDeviceURL = (telSysID: string, partitionID: string, deviceID: string): string =>
    actionDeviceActionURL(telSysID, partitionID, APIPathParams.MAKE_CALL, deviceID).constructURL()

// Transfer One Step.
export const transferOneStepStatusURL: URLConstructor = statusCallActionURL(APIPathParams.TRANSFER_ONE_STEP)
export const transferOneStepFromDeviceURL = (telSysID: string, partitionID: string, deviceID: string): string =>
    actionDeviceActionURL(telSysID, partitionID, APIPathParams.TRANSFER_ONE_STEP, deviceID).constructURL()
export const transferOneStepCurrentUser: string = currentUserActionURL(APIPathParams.TRANSFER_ONE_STEP).constructURL()

// Transfer One Step.
export const divertDeflectStatusURL: URLConstructor = statusCallActionURL(APIPathParams.DIVERT_DEFLECT)
export const divertDeflectFromDeviceURL = (telSysID: string, partitionID: string, deviceID: string): string =>
    actionDeviceActionURL(telSysID, partitionID, APIPathParams.DIVERT_DEFLECT, deviceID).constructURL()
export const divertDeflectCurrentUser: string = currentUserActionURL(APIPathParams.DIVERT_DEFLECT).constructURL()

// Inquiry transfer to.
export const inquiryTransferToStatusURL: URLConstructor = statusCallActionURL(APIPathParams.INQUIRY_TRANSFER_TO)
export const inquiryTransferToMainDeviceURL = (): string =>
    currentUserActionURL(APIPathParams.INQUIRY_TRANSFER_TO).constructURL()

// Pickup connection.=
export const pickupConnectionStatusURL: URLConstructor = statusCallActionURL(APIPathParams.PICKUP_CONNECTION)
export const pickupConnectionOnMainDeviceURL = (): string =>
    currentUserActionURL(APIPathParams.PICKUP_CONNECTION).constructURL()

// Set DND.
export const setDNDStatusURL = (dnd: boolean): URLConstructor =>
    statusCallActionURL(`${APIPathParams.SET_DND}${dnd ? DNDActions.DND_ON : DNDActions.DND_OFF}`)
export const setDNDMainDeviceURL = (telSysID: string, partitionID: string, deviceID: string, dnd: boolean): string =>
    actionDeviceActionURL(
        telSysID,
        partitionID,
        `${APIPathParams.SET_DND}${dnd ? DNDActions.DND_ON : DNDActions.DND_OFF}`,
        deviceID
    ).constructURL()

// Set ACD status.
export const setACDStatusStatusURL = (statusUpdateType: ACDStatusUpdates): URLConstructor =>
    statusCallActionURL(statusUpdateType)
export const setACDStatusAgentURLConstructor = (
    telSysID: string | number,
    partitionID: string | number,
    agentID: Nullable<string>,
    statusUpdateType: ACDStatusUpdates
): URLConstructor => actionAgentActionURL(telSysID, partitionID, agentID ?? '', statusUpdateType)
export const setACDStatusAgentURL = (
    telSysID: string,
    partitionID: string,
    agentID: Nullable<string>,
    statusUpdateType: ACDStatusUpdates
): string => setACDStatusAgentURLConstructor(telSysID, partitionID, agentID ?? '', statusUpdateType).constructURL()

// Call ctrl props.
export const callCtrlPropsURL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.CURRENT_USER,
    AkixiEntities.CALL_CTRL_PROPS
)

// Get CMEs.
export const getCMEURL = (fields?: string[]): URLConstructor =>
    cmeInternalBaseURL().appendQueryParams({
        [QueryParams.SORT]: SortingParameters.DESCRIPTION_ASC,
        [QueryParams.CME_FIELDS]: fields
    })

// Get All TelSys to user.
export const getAllTelSysAccessibleByUser = (): URLConstructor =>
    getAPIInternalBaseURL()
        .appendPathParams(AkixiEntities.TEL_SYS)
        .appendQueryParams({
            [QueryParams.RECURSIVE]: false,
            [QueryParams.SORT]: SortingParameters.DESCRIPTION_ASC
        })

// Get tel servers.
export const telServerURL = (
    cmeID: string,
    recursive?: boolean,
    telSysFields?: string[],
    ...licenceLevel: string[]
): URLConstructor =>
    getAPIInternalBaseURL()
        .appendPathParams(AkixiEntities.CME, cmeID, AkixiEntities.TEL_SYS)
        .appendQueryParams({
            [QueryParams.RECURSIVE]: `${recursive || false}`,
            [QueryParams.SORT]: SortingParameters.DESCRIPTION_ASC,
            [QueryParams.TEL_SYS_FIELDS]: telSysFields
                ?.map((telSysField: string): string => telSysField.trim())
                ?.join(','),
            ...(licenceLevel && licenceLevel.length > 0
                ? { [QueryParams.FILTER_BY_PARTITION_LICENCE]: licenceLevel.join(',') ?? '' }
                : {})
        })

// Get tel server.
export const telSysBaseURL: URLConstructor = getAPIInternalBaseURL().appendPathParams(AkixiEntities.TEL_SYS)
export const telSysURL = (telServerID: string | number, ...telSysFields: string[]): URLConstructor =>
    telSysBaseURL.appendPathParams(telServerID).appendQueryParams({
        ...(telSysFields && telSysFields.length > 0
            ? { [QueryParams.TEL_SYS_FIELDS]: telSysFields.join(',') }
            : undefined),
        // Check if telSysFields contains values from advanced fields and add advancedFields to the query params
        ...(Object.values(ADVANCED_FIELDS).some((field: string): boolean => telSysFields.includes(field))
            ? { [QueryParams.ADVANCED_FIELDS_PARAM]: true }
            : {})
    })
// Get partition.
export const partitionURL = (telServerID: number, partitionID: number): URLConstructor =>
    getAPIInternalBaseURL().appendPathParams(AkixiEntities.TEL_SYS, telServerID, AkixiEntities.PARTITION, partitionID)

// Get partitions.
export const partitionsURL = (telServerID: number): URLConstructor =>
    getAPIInternalBaseURL()
        .appendPathParams(AkixiEntities.TEL_SYS, telServerID, AkixiEntities.PARTITION)
        .appendQueryParams({ [QueryParams.SORT]: SortingParameters.DESCRIPTION_ASC })

// Get Group membership.
export const groupMembershipURL = (
    telServerID: number,
    partitionID: number,
    device: string,
    deviceFilter?: string[]
): URLConstructor =>
    getAPIInternalBaseURL()
        .appendPathParams(
            AkixiEntities.TEL_SYS,
            telServerID,
            AkixiEntities.PARTITION,
            partitionID,
            AkixiEntities.DEVICE,
            device,
            AkixiEntities.GROUP_MEMBERS
        )
        .appendQueryParams({ [QueryParams.DEVICE_TYPE_FILTER]: deviceFilter?.join('-') })

// Get agents.
export const agentAdminURL = (telSysID: number, partitionID: number, agentNumber: string): URLConstructor =>
    partitionUpdateURL(telSysID, partitionID).addPathParams(AkixiEntities.ACD_AGENTS, agentNumber)

export const agentsAdminURL = (telServerID: number, partitionID: number): URLConstructor =>
    getAPIAdminBasePath().appendPathParams(
        AkixiEntities.TEL_SYS,
        telServerID,
        AkixiEntities.PARTITION,
        partitionID,
        AkixiEntities.ACD_AGENTS
    )

// Get partition.
export const callRecordingIntegrationsURL = (cmeID: string, telServerID: number, partitionID: number): URLConstructor =>
    getAPIInternalBaseURL()
        .appendPathParams(
            AkixiEntities.CME,
            cmeID,
            AkixiEntities.TEL_SYS,
            telServerID,
            AkixiEntities.PARTITION,
            partitionID,
            APIPathParams.CALL_RECORDING_INTEGRATIONS
        )
        .appendQueryParams({
            [QueryParams.RECURSIVE]: 'true'
        })

// Get servlet config.
export const CONFIG_PASSWORD_URL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.CONFIG,
    AkixiEntities.PASSWORD
)
export const CONFIG_LANGUAGE_URL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.CONFIG,
    AkixiEntities.LANGUAGE
)

// Get partition by GUID.
export const partitionByGUIDURL = (partitionGUID: string): URLConstructor =>
    getAPIInternalBaseURL().appendPathParams(AkixiEntities.PARTITION, partitionGUID)

// Get default reports list.
export const defaultReportsListURL = (reportingLevel: string, telSysID?: number): URLConstructor =>
    getAPIInternalBaseURL()
        .appendPathParams(AkixiEntities.DEFAULT_REPORTS, reportingLevel)
        .appendQueryParams({
            [QueryParams.TEL_SYS_ID]: telSysID
        })

// Feature info.
export const FEATURE_INFO_URL: URLConstructor = getAPIInternalBaseURL().appendPathParams(APIPathParams.FEATURE)

// Insights.
export const insightsURL = (telSys: string | number, partition: string | number): URLConstructor =>
    partitionUpdateURL(telSys, partition).appendPathParams(APIPathParams.INSIGHTS, APIPathParams.REPORT)

// MFA.
export const mfaSecretCodeURL: URLConstructor = getCurrentUserURL().appendPathParams(
    MFAPathParams.MFA,
    MFAPathParams.SECRET_CODE
)

export const mfaSetupURL: URLConstructor = getCurrentUserURL().appendPathParams(MFAPathParams.MFA, MFAPathParams.SETUP)

export const mfaEnableURL: URLConstructor = getCurrentUserURL().appendPathParams(
    MFAPathParams.MFA,
    MFAPathParams.ENABLE
)

export const mfaOTPURL: URLConstructor = getCurrentUserURL().appendPathParams(MFAPathParams.MFA, MFAPathParams.OTP)

export const loginURL: URLConstructor = getAPIBasePath().appendPathParams(LogInOut.LOG_IN)

export const logoutURL: URLConstructor = getAPIBasePath().appendPathParams(LogInOut.LOG_OUT)

// Invite User
export const inviteUserURL: URLConstructor = getAPIIntegrationBaseURL().appendPathParams(
    APIPathParams.TRACKER,
    APIPathParams.INVITE,
    APIPathParams.FINISH
)

// Notifications settings
export const currentUserSettings: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.CURRENT_USER,
    APIPathParams.SETTINGS
)

export const notificationSettingsPushToken: URLConstructor = currentUserSettings.appendPathParams(
    APIPathParams.NOTIFICATION_PUSH_TOKEN
)

export const ssoLoginURL = (ssoCode: string): URLConstructor =>
    getAPIBasePath()
        .appendPathParams(SSOLogIn.SSO)
        .appendQueryParams({ [QueryParams.CODE]: ssoCode })

export const brandingURL = getAPIInternalBaseURL()
    .appendPathParams(APIPathParams.BRANDING)
    .appendQueryParams({ [QueryParams.DEFAULT_BRANDING_STYLE]: 'true' })

export const brandingsURL = getAPIInternalBaseURL()
    .appendPathParams(APIPathParams.BRANDING, APIPathParams.LIST)
    .appendQueryParams({ [QueryParams.DEFAULT_BRANDING_STYLE]: 'true' })

export const defaultReportCollectionNameURL = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.REPORT,
    APIPathParams.DEFAULT_REPORT
)

export const defaultReportSetCreationURL = (defaultReportType: string): URLConstructor =>
    getAPIInternalBaseURL()
        .appendPathParams(APIPathParams.REPORT, APIPathParams.REPORT_SET)
        .appendQueryParams({ [QueryParams.DEFAULT_REPORT_TYPE]: defaultReportType })

export const defaultReportCreationURL = (defaultReportType: string, defaultReportName: string): URLConstructor =>
    getAPIInternalBaseURL()
        .appendPathParams(APIPathParams.REPORT, APIPathParams.DEFAULT_REPORT)
        .appendQueryParams({
            [QueryParams.DEFAULT_REPORT_TYPE]: defaultReportType,
            [QueryParams.DEFAULT_REPORT_NAME]: defaultReportName
        })

export const moveReportsToBinMURL = (moveToBin: boolean): URLConstructor =>
    getAPIInternalBaseURL()
        .appendPathParams(APIPathParams.REPORT, APIPathParams.MOVE_TO_BIN)
        .appendQueryParams({ [QueryParams.MOVE_TO_BIN]: `${moveToBin || false}` })

export const broadworksAuthenticateURL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.BROADWORKS,
    APIPathParams.AUTHENTICATE
)

export const getDeviceByIDOnTelSysURL = (idOnTelSys: string): URLConstructor =>
    getAPIInternalBaseURL().appendPathParams(AkixiEntities.DEVICE, APIPathParams.ID_ON_TEL_SYS, idOnTelSys)

export const postBroadworksSignupURL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.BROADWORKS,
    APIPathParams.SIGNUP
)

export const getTimezoneDataURL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.UTILITIES,
    APIPathParams.TIMEZONE
)

export const TIMEZONE_DEFAULT_URL: URLConstructor = getTimezoneDataURL.appendPathParams(APIPathParams.DEFAULT)

export const LICENCE_URL: URLConstructor = getAPIInternalBaseURL().appendPathParams(APIPathParams.LICENCE)

export const getLicenceSettingsURL = (licenceSettings?: string[]): URLConstructor =>
    LICENCE_URL.appendPathParams(APIPathParams.SETTINGS).appendQueryParams({
        [QueryParams.LICENCE_SETTING_FIELDS]: licenceSettings?.join(',')
    })

export const getCallRecordingIntegrationURL = (telSysID: string | number): URLConstructor =>
    telSysURL(telSysID).appendPathParams(APIPathParams.CALL_RECORDING_INTEGRATIONS)

const INTEGRATION_TYPES: Record<string, string> = {
    [TelSysTypes.WEBEX_CALLING]: 'webexCalling',
    [TelSysTypes.MS_TEAMS]: 'MSTeams'
}

export const getOAuthConfigURL = (telSysType: TelSysTypes): URLConstructor =>
    getAPIBasePath().appendPathParams(
        APIPathParams.INTEGRATION,
        APIPathParams.OAUTH,
        INTEGRATION_TYPES[telSysType],
        APIPathParams.CONFIG
    )

export const getOAuthIntegrationStatus = (telSysType: string, sessionID: string | number): URLConstructor =>
    getAPIBasePath().appendPathParams(
        APIPathParams.INTEGRATION,
        APIPathParams.OAUTH,
        INTEGRATION_TYPES[telSysType],
        sessionID,
        APIPathParams.STATUS
    )

export const getSimulationModesURL = (): URLConstructor =>
    telSysBaseURL.appendPathParams(APIPathParams.CALL_CONTROL_OPTIONS)

export const cmeLicencesURL = (cmeID: string): URLConstructor =>
    cmeInternalGet(cmeID).appendPathParams(APIPathParams.LICENCES)

export const telSysLicencesURL = (telSysID: string | number): URLConstructor =>
    telSysByIDInteralURL(telSysID).appendPathParams(APIPathParams.LICENCES)

export const changeCurrentUserPasswordURL = (): URLConstructor =>
    getCurrentUserURL().appendPathParams(APIPathParams.CHANGE_PASSWORD)

export const passwordURL = (): URLConstructor => getAPIBasePath().appendPathParams(APIPathParams.PASSWORD)

export const generatePasswordURL = (): URLConstructor => passwordURL().appendPathParams(APIPathParams.GENERATE)

export const passwordStrengthURL = (): URLConstructor => passwordURL().appendPathParams(APIPathParams.STRENGTH)

export const telSysCallCaptureControlURL = (telSysID: string | number): URLConstructor =>
    telSysUpdateURL(telSysID).appendPathParams(APIPathParams.CALL_CAPTURE)

export const cmeCallCaptureControlURL = (cmeID: string): URLConstructor =>
    cmeDetailsByIDURL(cmeID).appendPathParams(APIPathParams.CALL_CAPTURE)

export const hideInsightsHub: URLConstructor = getCurrentUserURL().appendPathParams(APIPathParams.HIDE_INSIGHTS_HUB)

export const showInsightsHub: URLConstructor = getCurrentUserURL().appendPathParams(APIPathParams.SHOW_INSIGHTS_HUB)

export const PRESENCE_WEBSOCKET_URL: URLConstructor = URLConstructor.newWebsocketURL({
    pathParams: [APIElements.CCS, APIPathParams.WEBSOCKET, APIPathParams.PRESENCE]
})

export const deviceTypesURL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.REPORT,
    APIPathParams.FILTER_OPTION,
    APIPathParams.DEVICE_TYPES
)

export const LOCALES_URL: URLConstructor = getAPIInternalBaseURL().appendPathParams(APIPathParams.LOCALES)

// API Key URL under user URL.
export const getAPIKeyURL = (username: string, id?: string): URLConstructor =>
    getAPIInternalBaseURL().appendPathParams(
        APIPathParams.USER,
        username,
        APIPathParams.API_KEYS,
        ...(isStringNotEmpty(id) ? [id] : [])
    )

export const ROUTE_URL: URLConstructor = getAppV2APIBasePath().appendPathParams(APIPathParams.ROUTE)

export const AUTH_OUTFLOW_URL: URLConstructor = ROUTE_URL.appendPathParams(APIPathParams.AUTH_OUTFLOW)

export const acceptTermsOfServiceURL: URLConstructor = getCurrentUserURL().appendPathParams(
    APIPathParams.ACCEPT_TERMS_OF_SERVICE
)

export const rejectTermsOfServiceURL: URLConstructor = getCurrentUserURL().appendPathParams(
    APIPathParams.REJECT_TERMS_OF_SERVICE
)

export const LOGIN_LANDING_PAGE_FLAGS_URL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.LICENCE,
    APIPathParams.LOGIN_LANDING
)

export const userLoginLandingUserCheckURL = (username: string, cme?: string): URLConstructor =>
    getAPIBasePath().appendPathParams(APIPathParams.USER, username).appendQueryParams({ cme })

export const availableServicesForUser = (username: string, cme?: string): URLConstructor =>
    getAPIBasePath().appendPathParams(APIPathParams.USER, username, APIPathParams.SERVICES).appendQueryParams({ cme })

export const RESET_PASSWORD_URL: URLConstructor = getCurrentUserURL().appendPathParams(APIPathParams.RESET_PASSWORD)

export const SESSION_LANGUAGE_URL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.SESSION,
    APIPathParams.LANGUAGE
)

export const SSO_LOGIN_URL: URLConstructor = getAPIBasePath().appendPathParams(APIPathParams.LOGIN)

export const INTERNAL_LOGIN_URL: URLConstructor = getAPIBasePath().appendPathParams(
    APIPathParams.CURRENT_USER,
    APIPathParams.LOGIN
)

export const RECAPTCHA_CONFIG_URL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.CONFIG,
    APIPathParams.RECAPTCHA
)

export const FORGOT_CREDENTIALS_URL: URLConstructor = getCurrentUserURL().appendPathParams(
    APIPathParams.FORGOT_CREDENTIALS
)

export const USER_FAVOURITES_URL: URLConstructor = currentUserSettings.appendPathParams(APIPathParams.FAVOURITES)

export const USER_DARKMODE_URL: URLConstructor = currentUserSettings.appendPathParams(APIPathParams.DARK_MODE)

export const mappedTimezoneURL = (timezone: string): URLConstructor =>
    getAPIInternalBaseURL()
        .appendPathParams(APIPathParams.TIMEZONE)
        .appendQueryParams({
            [QueryParams.IANA]: timezone
        })

export const SESSION_TOKEN_URL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.SESSION,
    APIPathParams.TOKEN
)

export const getServletCommandURL = (branding: string, servletCmd: string): URLConstructor =>
    getBaseServletPath(branding?.replace(SLASH, EMPTY_STRING)).appendQueryParams({ ServletCmd: servletCmd })

export const USER_AGENTS_URL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.SESSION,
    APIPathParams.USER_AGENTS
)

export const USER_AGENT_URL: URLConstructor = getAPIInternalBaseURL().appendPathParams(
    APIPathParams.SESSION,
    APIPathParams.USER_AGENT
)

export const getBrandingConfigByCMEAndTypeURL = (cmeID: string, brandingType: string): URLConstructor =>
    getAPIInternalBaseURL().appendPathParams(AkixiEntities.CME, cmeID, APIPathParams.BRANDING_CONFIG, brandingType)

export const getBrandingIDSessionURL = (brandingID?: string): URLConstructor =>
    getAPIInternalBaseURL()
        .appendPathParams(APIPathParams.SESSION, APIPathParams.BRANDING)
        .appendQueryParams(
            isStringNotEmpty(brandingID)
                ? {
                      previewBrandingID: brandingID
                  }
                : {}
        )

export const getBrandingVideoUploadURL = (brandingID: string): URLConstructor =>
    getAPIInternalBaseURL()
        .appendPathParams(APIPathParams.BRANDING, APIPathParams.VIDEO)
        .appendQueryParams({ [QueryParams.BRANDING_ID]: brandingID })

export const getClientConfigurationURL = (
    telsysID: string | number,
    partition: string | number,
    deviceID: string,
    id?: string
): URLConstructor =>
    deviceURL(telsysID.toString(), partition.toString(), deviceID)
        .appendPathParams(APIPathParams.INTEGRATOR_CONFIG, id ?? EMPTY_STRING)
        .appendQueryParams({
            [QueryParams.INTEGRATOR_CLIENT_FIELDS]: 'Policies,Settings'
        })

export const getClientConfigurationListURL = (
    telsysID: string | number,
    partition: string | number,
    deviceID: string,
    offset?: number,
    limit?: number,
    sort?: string,
    search?: string
): URLConstructor =>
    deviceURL(telsysID.toString(), partition.toString(), deviceID)
        .appendPathParams(APIPathParams.INTEGRATOR_CONFIG)
        .appendQueryParams({
            [QueryParams.OFFSET]: offset,
            [QueryParams.LIMIT]: limit,
            [QueryParams.SORT]: sort,
            [QueryParams.SEARCH_QUERY]: search,
            [QueryParams.INTEGRATOR_CLIENT_FIELDS]: 'TelSysDescription,PartitionDescription,DeviceDescription'
        })

export const getCRMConfigurationURL = (
    telsysID: number,
    partition: number,
    deviceID: string,
    id?: string
): URLConstructor =>
    deviceURL(telsysID?.toString(), partition?.toString(), deviceID).appendPathParams(
        APIPathParams.CRM_CONFIGURATION,
        APIPathParams.CONFIGS,
        id ?? EMPTY_STRING
    )

export const getCRMConfigurationListURL = (
    telsysID: string | number,
    partition: string | number,
    deviceID: string,
    offset?: number,
    limit?: number,
    sort?: string,
    search?: string,
    crmType?: string
): URLConstructor =>
    deviceURL(telsysID.toString(), partition.toString(), deviceID)
        .appendPathParams(APIPathParams.CRM_CONFIGURATION, APIPathParams.CONFIGS)
        .appendQueryParams({
            [QueryParams.OFFSET]: offset,
            [QueryParams.LIMIT]: limit,
            [QueryParams.SORT]: sort,
            [QueryParams.SEARCH_QUERY]: search,
            [QueryParams.CRM_TYPE]: crmType === ANY_VALUE_STRING_NUMERIC ? undefined : crmType,
            [QueryParams.CRM_CONFIGURATION_FIELDS]:
                'TelSysDescription,PartitionDescription,DeviceDescription,CMEnvironmentID,TelSysID,DeviceNumber,PartIDInTelSys'
        })

export const getCRMTemplate = (crmType: string): URLConstructor =>
    getAPIInternalBaseURL().appendPathParams(
        APIPathParams.CRM_CONFIGURATION,
        APIPathParams.CONFIGS,
        APIPathParams.TEMPLATE,
        crmType
    )

export const getCRMTypesURL = (cmeID?: string, telsysID?: string): URLConstructor =>
    getAPIInternalBaseURL()
        .appendPathParams(APIPathParams.CRM_CONFIGURATION, APIPathParams.TYPES)
        .appendQueryParams({
            [QueryParams.CME_ID]: cmeID,
            [QueryParams.TEL_SYS_ID]: telsysID
        })
